import React, { useState } from 'react';
import {
    DropdownSelect,
    type SingleValue,
    type OptionProps,
    type GroupBase,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { makeObservable, observable, computed, action } from 'mobx';
import { useAppStateContext } from 'src/AppState/AppState';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import confirmation from 'src/utils/confirm.jsx';
import addError from 'src/utils/error.jsx';
import { getTranslationFor } from 'src/locale/lang';
import { PaymentProvidersType } from 'src/AppState/ConfigState/types';
import { getSavedCards } from 'src_server/types';
import {
    SelectInputContainer,
    SelectInputWrapper,
    OptionWrapper,
    OptionWithDeleteWrapper,
    DeleteButton,
    getPaymentMethodDropdownStyles,
} from './PaymentMethodDropdown.style';
import { InputLabelInside } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { DateTime } from 'src_common/utils/time/time';
import { SavedCardInterface } from 'src_server/types/getSavedCards';

export const OPEN_BANKING_CARD = 'OpenBanking';
export const DefaultYaspaMethod: SavedCardInterface = {
    id: OPEN_BANKING_CARD,
    number: OPEN_BANKING_CARD,
    provider: 'yaspa',
    type: OPEN_BANKING_CARD,
};

interface PaymentMethodDropdownPropsType {
    allowAddingMethod?: boolean;
    className?: string;
    onChange?: (value: string) => void;
    onValue?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    value: string;
    customerId: number;
    paymentProvider: PaymentProvidersType;
}

interface ParsedOptionType {
    value: string;
    method: SavedCardInterface | undefined;
}

const defaultMethod = { value: '', method: undefined };

class DropdownState {
    @observable public isFocused: boolean = false;
    @observable public cardDetails: ParsedOptionType | undefined;

    public constructor(
        public props: PaymentMethodDropdownPropsType,
        public models: ModelsState,
        public sdkStaff: SdkStaff,
        public trpcClient: TrpcClient,
        public customerId: number,
        public paymentProvider: PaymentProvidersType
    ) {
        makeObservable(this);
    }

    @action public onChange = (parsedOption: SingleValue<ParsedOptionType>): void => {
        if (this.props.onChange === undefined) {
            return;
        }

        this.cardDetails = parsedOption === null ? undefined : parsedOption;
        this.props.onChange(parsedOption?.value ?? defaultMethod.value);
    };

    @computed public get cardList(): getSavedCards.TSuccessResponse {
        const pspRequest =
            this.models
                .getUserModel(this.customerId)
                .listCards.cardListAutoMap.get(this.paymentProvider.type)
                .getReady() ?? null;

        const yaspaRequest =
            this.models.getUserModel(this.customerId).listCards.cardListAutoMap.get('yaspa').getReady() ?? null;
        const openBankingMehtod =
            yaspaRequest?.responseStatus === 'success' && yaspaRequest.response.length > 0 ? [DefaultYaspaMethod] : [];

        return pspRequest?.responseStatus === 'success' ? [...pspRequest.response, ...openBankingMehtod] : [];
    }

    @action public onFocus = (): void => {
        this.isFocused = true;
    };

    @action public onBlur = (): void => {
        this.isFocused = false;

        if (this.props.onBlur === undefined) {
            return;
        }

        this.props.onBlur();
    };

    private parseOptions = (options: getSavedCards.TSuccessResponse | null): ParsedOptionType[] => {
        if (options === null) {
            return [];
        } else {
            return options.map((v) => ({
                value: v.id,
                method: v,
            }));
        }
    };

    @action public renderOptions = (): ParsedOptionType[] => {
        const options = this.cardList;

        const parsedValue = this.parseOptions(options);

        if (this.props.allowAddingMethod === true) {
            parsedValue.unshift({
                value: 'none',
                method: undefined,
            });
            return parsedValue;
        }
        return parsedValue;
    };

    @computed public get className(): string {
        return classNames([this.props.className, 'payment-method-dropdown']);
    }

    public formatCardNumber(cardNumber: string): string | undefined {
        return `**** **** **** ${cardNumber.substring(cardNumber.length - 4)}`;
    }

    public formatExpiresDate(date: string | null | undefined): string | undefined {
        if (date === null || date === undefined) {
            return undefined;
        }

        return DateTime.from(date)?.format('MM/YY');
    }

    @action public onRemoveCard = async (method: SavedCardInterface | undefined): Promise<void> => {
        if (method === undefined) {
            addError('method to remove is undefined');
            return;
        }

        const result = await confirmation(
            getTranslationFor('PAYMENT_METHOD_REMOVE_CONFIRMATION_MESSAGE') + method.name,
            method.id
        );

        //@ts-expect-error
        if (result === method.id) {
            try {
                if (method.provider === 'realex') {
                    await this.sdkStaff.deleteCardRealex({ card_id: method.id, customer_id: this.customerId });
                } else if (method.provider === 'paysafe') {
                    await this.trpcClient.client.paysafeRouter.deleteCardPaysafe.mutate({
                        customerId: this.customerId.toString(),
                        cardId: method.id,
                    });
                }

                await this.models
                    .getUserModel(this.customerId)
                    .listCards.cardListAutoMap.get(this.paymentProvider.type)
                    .refresh();
            } catch (err) {
                addError(err);
            }
        }
    };
}

export const PaymentMethodDropdown = observer((props: PaymentMethodDropdownPropsType) => {
    const { models, sdkStaff, common } = useAppStateContext();
    const [dropdownState] = useState(
        () => new DropdownState(props, models, sdkStaff, common.trpcClient, props.customerId, props.paymentProvider)
    );

    const CustomOption = (
        optionProps: OptionProps<ParsedOptionType, false, GroupBase<ParsedOptionType>>
    ): JSX.Element => {
        const { data, innerRef, innerProps, isSelected } = optionProps;

        if (data.method === undefined) {
            return (
                <OptionWrapper
                    ref={innerRef}
                    {...innerProps}
                    isSelected={isSelected}
                    role='option'
                >
                    <span>New payment method</span>
                </OptionWrapper>
            );
        } else {
            if (data.value === OPEN_BANKING_CARD) {
                return (
                    <OptionWrapper
                        ref={innerRef}
                        {...innerProps}
                        isSelected={isSelected}
                        role='option'
                    >
                        <span>Open Banking</span>
                    </OptionWrapper>
                );
            }

            return (
                <OptionWithDeleteWrapper
                    ref={innerRef}
                    {...innerProps}
                    isSelected={isSelected}
                    role='option'
                >
                    <span>
                        {data.value === 'OpenBanking'
                            ? 'OpenBanking'
                            : `${dropdownState.formatCardNumber(data.method.number)}, ${dropdownState.formatExpiresDate(data.method.expires)}`}
                    </span>
                    <DeleteButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void dropdownState.onRemoveCard(data.method);
                        }}
                    >
                        x
                    </DeleteButton>
                </OptionWithDeleteWrapper>
            );
        }
    };

    if (dropdownState.cardList.length === 0) {
        return <div className='message'>There are no saved debit cards</div>;
    }

    // Cache options to avoid multiple calls
    const options = dropdownState.renderOptions();
    const foundValue = options.find((opt) => opt.value === props.value);
    const selectedValue = foundValue === undefined ? null : foundValue;

    const wrapperClassName = classNames(['element', 'hasLabel', { empty: selectedValue === null }]);

    return (
        <SelectInputWrapper className={wrapperClassName}>
            <SelectInputContainer>
                <DropdownSelect<ParsedOptionType>
                    options={options}
                    value={selectedValue}
                    className={dropdownState.className}
                    onChange={dropdownState.onChange}
                    onBlur={dropdownState.onBlur}
                    onFocus={dropdownState.onFocus}
                    components={{
                        Option: CustomOption,
                    }}
                    customStyles={getPaymentMethodDropdownStyles<ParsedOptionType, false>()}
                    getOptionValue={(option): string => option.value}
                    getOptionLabel={(option): string => {
                        if (option.method === undefined) {
                            return 'New payment method';
                        } else if (option.value === OPEN_BANKING_CARD) {
                            return 'Open Banking';
                        } else {
                            return `${dropdownState.formatCardNumber(option.method.number)}, ${dropdownState.formatExpiresDate(option.method.expires)}`;
                        }
                    }}
                    hasInternalLabel={true}
                />
                <InputLabelInside>Payment method</InputLabelInside>
            </SelectInputContainer>
        </SelectInputWrapper>
    );
});
